import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";

const TYPE_LABEL = {
  convention: "Convention de mise à disposition",
  devis: "Devis prévisionnel",
  acompte: "Appel à contribution (acompte)",
  facture: "Facture / Appel à contribution",
  "feuille-temps": "Feuille de temps mensuelle",
};

export default function Verify() {
  const { docType, docId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Endpoint public — pas besoin d'être authentifié
    api.get(`/verify/${docType}/${docId}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ valid: false, message: "Erreur réseau" }))
      .finally(() => setLoading(false));
  }, [docType, docId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  const valid = data?.valid;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="verify-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/" className="text-sm text-slate-500 hover:text-[#C5A059]">Retour</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className={`rounded-xl border-2 p-6 ${valid ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`}>
          <div className="flex items-start gap-4">
            {valid
              ? <CheckCircle2 className="h-10 w-10 text-emerald-600 shrink-0" />
              : <XCircle className="h-10 w-10 text-red-600 shrink-0" />}
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                {valid ? "Document authentique" : "Document non reconnu"}
              </h1>
              <p className={`mt-1 ${valid ? "text-emerald-700" : "text-red-700"}`}>
                {valid
                  ? `Émis par O'SCOP GE — SCIC SAS OBJECTIF SCOP OUTREMER (SIRET 903 459 139 00080)`
                  : (data?.message || "Ce document n'a pas été émis par O'SCOP GE.")}
              </p>
            </div>
          </div>
        </div>

        {valid && (
          <div className="mt-8 bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#C5A059]">Type de document</div>
              <div className="text-lg font-semibold text-slate-900 mt-1">{TYPE_LABEL[data.type] || data.type}</div>
              <div className="text-xs text-slate-500 mt-1 font-mono">{data.reference}</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
              <Field label="Entreprise" value={data.entreprise?.raison_sociale} />
              <Field label="SIRET" value={data.entreprise?.siret} />
              <Field label="Territoire" value={data.entreprise?.territoire} />
              {data.type === "convention" ? (
                <>
                  <Field label="Statut" value={data.statut} />
                  <Field label="Période" value={`${data.periode?.debut} → ${data.periode?.fin}`} />
                  <Field label="Tarif horaire" value={data.tarif_horaire ? `${Number(data.tarif_horaire).toFixed(2)} €` : "—"} />
                </>
              ) : data.type === "feuille-temps" ? (
                <>
                  <Field label="Salarié" value={data.salarie} />
                  <Field label="Période" value={data.periode} />
                </>
              ) : (
                <>
                  <Field label="Période" value={data.periode} />
                  <Field label="Type d'appel" value={data.type_appel} />
                  <Field label="Montant" value={data.montant ? `${Number(data.montant).toLocaleString("fr-FR")} €` : "—"} />
                  <Field label="Statut" value={data.statut} />
                  <Field label="Date d'émission" value={data.date_emission} />
                </>
              )}
            </div>

            {data.signatures?.length > 0 && (
              <div className="pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[#C5A059] mb-3">
                  <ShieldCheck className="h-3.5 w-3.5" /> Signatures électroniques
                </div>
                <div className="space-y-2">
                  {data.signatures.map((sig, i) => (
                    <div key={i} className="bg-[#FDF9F1] border border-[#C5A059]/30 rounded-lg p-3">
                      <div className="flex justify-between flex-wrap gap-2">
                        <div>
                          <div className="font-semibold text-slate-900">{sig.nom}</div>
                          <div className="text-xs text-slate-500">{sig.qualite || "—"}</div>
                        </div>
                        <div className="text-xs text-slate-500">
                          {sig.signed_at?.slice(0,19).replace("T", " ")}
                        </div>
                      </div>
                      <div className="mt-2 text-[10px] font-mono text-slate-400 break-all">SHA-256: {sig.hash}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-slate-100 text-xs text-slate-500">
              Émetteur officiel : <strong className="text-slate-700">{data.emetteur?.nom}</strong> — SIRET {data.emetteur?.siret}.
              Vérification effectuée le {new Date().toLocaleString("fr-FR")}.
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5">{value || "—"}</div>
    </div>
  );
}
